/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
// import { OrgUnitContact } from '@msdyn365-commerce/retail-proxy';
// import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import * as React from 'react';
import { IDealerLocation } from '../../eve-map';

/**
 * Store Locator line item resources.
 */
export interface IDealerLocatorLocationLineItemResources {
    contactInfoHeader: string;
    dealerDetailsHeader: string;
    dealerDetailsManufacturerCodeText: string;
    dealerDetailsLaborRateText: string;
    dealerDetailsDescriptionText: string;
    dealerDetailsNotesText: string;
    dealerDetailsContractCodeText: string;
}

/**
 * Store Locator line item props.
 */
export interface IDealerLocatorLocationLineItemProps {
    location: IDealerLocation;
    resources: IDealerLocatorLocationLineItemResources;
    index: string;
    preferredDistanceUnit: string;
}

/**
 * Function to render store hours.
 * @param storeHours - Store Hours object.
 * @param location - Store Unit.
 * @param resources - Resources.
 * @returns - Jsx element.
 */
const renderDealerDetails = (location: IDealerLocation, resources: IDealerLocatorLocationLineItemResources): JSX.Element | null => {
    const {
        dealerDetailsDescriptionText,
        // dealerDetailsLaborRateText,
        // dealerDetailsManufacturerCodeText
        dealerDetailsContractCodeText
    } = resources;

    // console.log('Rendering Details');
    if (location.ContractList) {
        // console.log(location);
        return (
            <div className='ms-store-select__store-hours-details'>
                {location.ContractList.length > 0
                    ? location.ContractList.map(mfrCode => {
                          return (
                              <>
                                  <div className='ms-store-select__store-hours-details-group'>
                                      <div className='ms-store-select__store-hours-details-group-mfr-code'>
                                          <p className='ms-store-select__store-hours-details-group-mfr-code--data'>
                                              {`${mfrCode.ManufacturerCode} - ${mfrCode.ManufacturerName}`}
                                          </p>
                                      </div>
                                      <div className='ms-store-select__store-hours-details-group-contract-code'>
                                          <p className='ms-store-select__store-hours-details-group-contract-code--data'>
                                              <span className='ms-store-select__store-hours-details-group-contract-code--data-heading'>
                                                  {dealerDetailsContractCodeText}
                                              </span>
                                              {mfrCode.ContractCode}
                                          </p>
                                      </div>
                                      <div className='ms-store-select__store-hours-details-group-desc'>
                                          <p className='ms-store-select__store-hours-details-group-desc--data'>
                                              <span className='ms-store-select__store-hours-details-group-desc--data-heading'>
                                                  {dealerDetailsDescriptionText}
                                              </span>
                                              {mfrCode.ContractDescription}
                                          </p>
                                      </div>
                                  </div>
                              </>
                          );
                      })
                    : null}
            </div>
        );
    }
    return <div className='ms-store-select__store-hours-details'></div>;
};

/**
 * Function to retrun distance measure unit.
 * @param distance - Store location within radius.
 * @param preferredDistanceUnit - Unit configuration.
 * @param isLocationDisabled - Flag to check user location.
 * @returns - Unit of distance.
 */
export const buildDistanceString = (
    distance: number | undefined,
    preferredDistanceUnit: string,
    isLocationDisabled?: boolean
): string | undefined => {
    if (distance === undefined || isLocationDisabled) {
        return undefined;
    } else if (distance < 1) {
        return `(<1 ${preferredDistanceUnit})`;
    }
    return `(${Math.floor(distance)} ${preferredDistanceUnit})`;
};

/**
 * Function to store contact.
 * @param contacts - Store location within radius.
 * @returns - Return primary contact number of store.
 */
// const extractPhoneNumber = (contacts: OrgUnitContact[] | undefined): string | undefined => {
//     if (!contacts) {
//         return undefined;
//     }

//     const allPhoneContacts = contacts.filter(contact => contact.ContactTypeValue === 1 && !contact.IsPrivate);

//     if (ArrayExtensions.hasElements(allPhoneContacts)) {
//         const primaryPhoneContact = allPhoneContacts.find(contact => contact.IsPrimary);

//         if (primaryPhoneContact) {
//             return primaryPhoneContact.Locator;
//         }

//         return allPhoneContacts[0].Locator;
//     }

//     return undefined;
// };

/**
 * Renders store location line items.
 * @param props - Store location line items props.
 * @returns - HTML.
 */
const dealerLocatorLocationLineItem: React.FC<IDealerLocatorLocationLineItemProps> = (props: IDealerLocatorLocationLineItemProps) => {
    const { location, resources, index } = props;

    // console.log(location);

    const storeName: string | undefined = location.OrgUnitName;
    const distanceAsString: string | undefined = buildDistanceString(location.Distance, props.preferredDistanceUnit);
    const storeAddress: string | undefined = location.Address;
    // TODO: Need to fix the phone number portion when we actually have a phone number to use
    // const phoneNumber: string | undefined = extractPhoneNumber(location.PhoneNumber);

    const channelId: number | undefined = location.ChannelId;

    return (
        <div className='ms-store-select__location-line-item'>
            <div className='ms-store-select__location-line-item-header'>
                {/* {(shouldShowIndex || storeLocatorView) && <span className='ms-store-select__location-line-item-store-index'>{index}</span>} */}
                {<span className='ms-store-select__location-line-item-store-index'>{index}</span>}
                <span className='ms-store-select__location-line-item-store-name'>{storeName}</span>
                {distanceAsString ? <span className='ms-store-select__location-line-item-store-distance'> {distanceAsString}</span> : false}
            </div>
            <div id={`eve-store-${channelId}`} className='ms-store-select__location-line-item-content'>
                <div className='ms-store-select__location-line-item-contact-info'>
                    {/* <p className='ms-store-select__location-line-header'>{resources.contactInfoHeader}</p> */}
                    {storeAddress && (
                        <div className='ms-store-select__location-line-shop-address-section'>
                            <span className='msi msi-map-marker-alt ms-store-select__location-line-shop-address-glyph-icon' />
                            <span className='ms-store-select__location-line-shop-address-text'>{storeAddress}</span>
                        </div>
                    )}
                    {/* TODO: Fix the phone number section when/if there is phone number data to display */}
                    {/* {phoneNumber && (
                        <div className='ms-store-select__location-line-shop-phone-section'>
                            <span className='msi msi-phone ms-store-select__location-line-shop-phone-icon' />
                            <span className='ms-store-select__location-line-shop-phone-text'>{phoneNumber}</span>
                        </div>
                    )} */}
                </div>
                <div className='ms-store-select__location-line-item-store-hours'>
                    <p className='ms-store-select__location-line-header'>{resources.dealerDetailsHeader}</p>
                    {renderDealerDetails(location, resources)}
                </div>
            </div>
        </div>
    );
};

/**
 * Store location line item component.
 */
export const DealerLocatorLocationLineItemComponent = dealerLocatorLocationLineItem as (
    props: IDealerLocatorLocationLineItemProps
) => JSX.Element;
